import type { AgentStreamEvent, ClientAgentMessage, ProposalStatus, ToolCallInfo } from "@/types/agent";

export interface SseHandlers {
  updateLastAssistant: (updater: (msg: ClientAgentMessage) => ClientAgentMessage) => void;
  setConversationId: (id: string) => void;
  onUiCommand: (e: Extract<AgentStreamEvent, { type: "ui_command" }>) => void;
  onDone: (usage: { inputTokens: number; outputTokens: number; costUsd?: number }) => void;
}

export function handleSseEvent(e: AgentStreamEvent, h: SseHandlers): void {
  switch (e.type) {
    case "conversation_id":
      h.setConversationId(e.conversationId);
      return;

    case "text_delta":
      h.updateLastAssistant((msg) => ({ ...msg, content: msg.content + e.content }));
      return;

    case "tool_call_start": {
      const tc: ToolCallInfo = { toolCallId: e.toolCallId, toolName: e.toolName, status: "running" };
      h.updateLastAssistant((msg) => ({ ...msg, toolCalls: [...(msg.toolCalls ?? []), tc] }));
      return;
    }

    case "tool_call_end":
      h.updateLastAssistant((msg) => ({
        ...msg,
        toolCalls: (msg.toolCalls ?? []).map((tc) =>
          tc.toolCallId === e.toolCallId ? { ...tc, status: "complete" as const } : tc,
        ),
      }));
      return;

    case "tool_result":
      h.updateLastAssistant((msg) => ({
        ...msg,
        toolCalls: (msg.toolCalls ?? []).map((tc) =>
          tc.toolCallId === e.toolCallId
            ? { ...tc, summary: e.summary, status: e.isError ? ("error" as const) : ("complete" as const) }
            : tc,
        ),
      }));
      return;

    case "proposal":
      h.updateLastAssistant((msg) => ({ ...msg, proposal: { ...e.proposal, status: "pending" as ProposalStatus } }));
      return;

    case "suggestions":
      h.updateLastAssistant((msg) => ({ ...msg, suggestions: e.suggestions }));
      return;

    case "ui_command":
      h.onUiCommand(e);
      return;

    case "error":
      h.updateLastAssistant((msg) => ({ ...msg, content: msg.content || e.error, isError: true }));
      return;

    case "sensitivity_decision":
      h.updateLastAssistant((msg) => ({ ...msg, sensitivityTier: e.tier }));
      return;

    case "done":
      h.onDone(e.usage);
      return;
  }
}
